import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "download-limit-indicator"
}
const _hoisted_2 = {
  key: 0,
  class: "authenticated-info"
}
const _hoisted_3 = {
  key: 1,
  class: "anonymous-download-status"
}
const _hoisted_4 = { class: "flex items-center space-x-2" }
const _hoisted_5 = {
  key: 0,
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
  "stroke-width": "2",
  d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
}
const _hoisted_6 = {
  key: 1,
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
  "stroke-width": "2",
  d: "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
}
const _hoisted_7 = { class: "mt-2" }
const _hoisted_8 = { class: "flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1" }
const _hoisted_9 = { class: "w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2" }
const _hoisted_10 = {
  key: 0,
  class: "mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800"
}

import { computed, onMounted, ref } from 'vue';
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';

interface Props {
  showIndicator?: boolean;
  compact?: boolean;
}

interface Emits {
  (e: 'create-account'): void;
  (e: 'download-limit-reached'): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DownloadLimitIndicator',
  props: {
    showIndicator: { type: Boolean, default: true },
    compact: { type: Boolean, default: false }
  },
  emits: ["create-account", "download-limit-reached"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const emit = __emit;

// Reactive state
const downloadInfo = ref({
  remaining: 3,
  total: 3,
  isAuthenticated: false
});

// Computed properties
const usedDownloads = computed(() => {
  return downloadInfo.value.total - downloadInfo.value.remaining;
});

const progressPercentage = computed(() => {
  if (downloadInfo.value.total === 0) return 0;
  return (usedDownloads.value / downloadInfo.value.total) * 100;
});

const hasDownloadsRemaining = computed(() => {
  return downloadInfo.value.remaining > 0;
});

const statusMessage = computed(() => {
  if (hasDownloadsRemaining.value) {
    return `${downloadInfo.value.remaining} free download${downloadInfo.value.remaining === 1 ? '' : 's'} remaining today`;
  } else {
    return 'No free downloads remaining';
  }
});

const subMessage = computed(() => {
  if (hasDownloadsRemaining.value) {
    return 'Create an account for unlimited downloads';
  } else {
    return 'Create an account to continue downloading';
  }
});

const buttonText = computed(() => {
  return hasDownloadsRemaining.value ? 'Sign Up' : 'Sign Up Now';
});

// Methods
const updateDownloadInfo = () => {
  downloadInfo.value = centralizedImageSecurity.getRemainingDownloads();
  
  // Emit event if no downloads remaining
  if (!downloadInfo.value.isAuthenticated && downloadInfo.value.remaining === 0) {
    emit('download-limit-reached');
  }
};

// Lifecycle
onMounted(() => {
  updateDownloadInfo();
  
  // Update download info every 30 seconds to catch changes
  setInterval(updateDownloadInfo, 30000);
});

// Expose method for parent components to refresh the info
__expose({
  refresh: updateDownloadInfo
});

return (_ctx: any,_cache: any) => {
  return (_ctx.showIndicator)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (downloadInfo.value.isAuthenticated)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[1] || (_cache[1] = [
              _createElementVNode("div", { class: "flex items-center space-x-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg" }, [
                _createElementVNode("svg", {
                  class: "w-4 h-4 text-green-500",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ]),
                _createElementVNode("p", { class: "text-sm text-green-800 dark:text-green-200" }, " Unlimited downloads available ")
              ], -1)
            ])))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", {
                class: _normalizeClass([
        'flex items-center justify-between p-3 rounded-lg border transition-all duration-300',
        hasDownloadsRemaining.value 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      ])
              }, [
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(), _createElementBlock("svg", {
                    class: _normalizeClass([
            'w-5 h-5',
            hasDownloadsRemaining.value ? 'text-blue-500' : 'text-red-500'
          ]),
                    fill: "none",
                    stroke: "currentColor",
                    viewBox: "0 0 24 24"
                  }, [
                    (hasDownloadsRemaining.value)
                      ? (_openBlock(), _createElementBlock("path", _hoisted_5))
                      : (_openBlock(), _createElementBlock("path", _hoisted_6))
                  ], 2)),
                  _createElementVNode("div", null, [
                    _createElementVNode("p", {
                      class: _normalizeClass([
              'text-sm font-medium',
              hasDownloadsRemaining.value 
                ? 'text-blue-900 dark:text-blue-100' 
                : 'text-red-900 dark:text-red-100'
            ])
                    }, _toDisplayString(statusMessage.value), 3),
                    _createElementVNode("p", {
                      class: _normalizeClass([
              'text-xs',
              hasDownloadsRemaining.value 
                ? 'text-blue-700 dark:text-blue-300' 
                : 'text-red-700 dark:text-red-300'
            ])
                    }, _toDisplayString(subMessage.value), 3)
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('create-account'))),
                  class: _normalizeClass([
            'px-3 py-1.5 text-white text-xs font-medium rounded-md transition-colors',
            hasDownloadsRemaining.value 
              ? 'bg-blue-500 hover:bg-blue-600' 
              : 'bg-red-500 hover:bg-red-600'
          ])
                }, _toDisplayString(buttonText.value), 3)
              ], 2),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _cache[2] || (_cache[2] = _createElementVNode("span", null, "Free Downloads Used", -1)),
                  _createElementVNode("span", null, _toDisplayString(usedDownloads.value) + "/" + _toDisplayString(downloadInfo.value.total), 1)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", {
                    class: _normalizeClass([
              'h-2 rounded-full transition-all duration-300',
              hasDownloadsRemaining.value ? 'bg-blue-500' : 'bg-red-500'
            ]),
                    style: _normalizeStyle({ width: `${progressPercentage.value}%` })
                  }, null, 6)
                ])
              ]),
              (downloadInfo.value.remaining === 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[3] || (_cache[3] = [
                    _createElementVNode("div", { class: "flex items-center space-x-2" }, [
                      _createElementVNode("svg", {
                        class: "w-4 h-4 text-orange-500",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                        })
                      ]),
                      _createElementVNode("p", { class: "text-sm text-orange-800 dark:text-orange-200" }, " This is your last free download today! ")
                    ], -1)
                  ])))
                : _createCommentVNode("", true)
            ]))
      ]))
    : _createCommentVNode("", true)
}
}

})